import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    DndContext,
    closestCenter,
    TouchSensor,
    MouseSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, FileText, CheckCircle, Trash2, Loader2 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { uploadFiles, FileToUpload } from '../ScanningSlice';

interface PreviewModalProps {
    isOpen: boolean;
    initialImages: string[];
    onClose: () => void;
    onBack: () => void;
    subjectName?: string;
    subjectCode?: string;
}

const SortableImageItem = ({ id, image, index, onRemove }: { id: string, image: string, index: number, onRemove: (id: string) => void }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
        opacity: isDragging ? 0.8 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="relative aspect-[3/4] bg-secondary/20 border border-border rounded-xl overflow-hidden group shadow-sm hover:shadow-md transition-shadow select-none cursor-grab active:cursor-grabbing"
        >
            <img src={image} alt={`Page ${index + 1}`} className="w-full h-full object-cover pointer-events-none" />

            {/* Number Badge */}
            <div className="absolute top-2 left-2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm shadow-md z-20">
                {index + 1}
            </div>

            {/* Remove Button */}
            <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(id);
                }}
                className="absolute bottom-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity z-20 shadow-sm"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
};

export const PreviewModal: React.FC<PreviewModalProps> = ({
    isOpen,
    initialImages,
    onClose,
    subjectName = 'Scanned Document',
    subjectCode = 'SCAN-001'
}) => {
    const dispatch = useAppDispatch();
    const { isUploading, uploadProgress } = useAppSelector(
        (state) => state.scanning
    );

    // Store images mapping to IDs to handle reordering efficiently
    const [items, setItems] = useState(() => initialImages.map((img, i) => ({ id: `img-${i}-${Date.now()}`, src: img })));
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Sensors optimized for Mobile (Touch) and Desktop (Mouse)
    const sensors = useSensors(
        useSensor(TouchSensor, {
            // Press delay required to distinguish from scrolling
            activationConstraint: {
                delay: 200,
                tolerance: 8,
            },
        }),
        useSensor(MouseSensor, {
            // Require the mouse to move by 10 pixels before activating
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleRemoveItem = (id: string) => {
        setItems(items.filter(item => item.id !== id));
    };

    const generatePDF = () => {
        // PDF Simulation Preview - Just show the modal which renders images
        setShowPdfPreview(true);
    };

    const handleSubmit = async () => {
        setSubmitError(null);
        try {
            // Generate PDF from images
            const doc = new jsPDF();

            items.forEach((item, index) => {
                if (index > 0) doc.addPage();

                const imgProps = doc.getImageProperties(item.src);
                const pdfWidth = doc.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                doc.addImage(item.src, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            });

            // Convert PDF to Blob and then to File
            const pdfBlob = doc.output('blob');
            const fileName = `${subjectCode}_${Date.now()}.pdf`;
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

            // Prepare file for upload
            const fileToUpload: FileToUpload = {
                file_name: fileName,
                file_type: 'application/pdf',
                file: pdfFile,
            };

            // Upload using Redux action
            await dispatch(
                uploadFiles({
                    subject_name: subjectName,
                    paper_code: subjectCode,
                    files: [fileToUpload],
                })
            ).unwrap();

            // Success - Close modal immediately to show main SuccessModal from parent
            onClose();

        } catch (error: any) {
            setSubmitError(error || 'Failed to complete upload process');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            {/* Toast Notifications */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[110] w-full max-w-md px-4 flex flex-col gap-2">
                {submitError && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="bg-red-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center justify-between border border-red-600"
                    >
                        <span className="font-medium text-sm">{submitError}</span>
                        <button onClick={() => setSubmitError(null)} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-card w-full max-w-5xl h-[100dvh] md:h-[90vh] rounded-none md:rounded-2xl flex flex-col overflow-hidden shadow-2xl border-0 md:border border-border relative"
            >
                {/* Header */}
                <div className="p-4 md:p-6 border-b border-border flex justify-between items-start bg-card shrink-0">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                            Review Scanned Pages
                        </h2>
                        <p className="text-muted-foreground text-xs md:text-sm mt-1">Drag to reorder pages. Ensure correct sequence.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full transition-colors -mr-2 -mt-2">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* PDF Preview Modal Overlay - Simulated for better Mobile Compatibility */}
                    {showPdfPreview && (
                        <div className="absolute inset-0 z-50 bg-zinc-900/95 flex flex-col animate-in fade-in duration-200">
                            <div className="flex justify-between items-center text-white p-4 shrink-0 bg-black/40 backdrop-blur-md border-b border-white/10">
                                <h3 className="text-lg md:text-xl font-bold flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-red-500" />
                                    PDF Preview
                                </h3>
                                <button
                                    onClick={() => setShowPdfPreview(false)}
                                    className="bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col items-center gap-6">
                                {items.map((item, idx) => (
                                    <div key={item.id} className="w-full max-w-3xl bg-white shadow-2xl relative">
                                        <img
                                            src={item.src}
                                            alt={`Page ${idx + 1}`}
                                            className="w-full h-auto block"
                                        />
                                        <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-1 rounded backdrop-blur-sm">
                                            Page {idx + 1}
                                        </div>
                                    </div>
                                ))}
                                <div className="text-zinc-500 text-sm pb-8">End of Document</div>
                            </div>
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-6 bg-secondary/10">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={items.map(i => i.id)}
                                strategy={rectSortingStrategy}
                            >
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                                    {items.map((item, index) => (
                                        <SortableImageItem
                                            key={item.id}
                                            id={item.id}
                                            image={item.src}
                                            index={index}
                                            onRemove={handleRemoveItem}
                                        />
                                    ))}

                                    {/* Add Placeholder? Or just empty state if 0 */}
                                    {items.length === 0 && (
                                        <div className="col-span-full h-40 flex items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-xl">
                                            No images selected. Go back to camera.
                                        </div>
                                    )}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 md:p-6 border-t border-border bg-card flex flex-col-reverse md:flex-row justify-between items-center gap-4 shrink-0">
                    <button
                        onClick={generatePDF}
                        className="w-full md:w-auto px-6 py-3 md:py-2.5 rounded-lg border border-border font-medium hover:bg-secondary transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                        <FileText className="w-4 h-4" />
                        PDF Preview
                    </button>

                    <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">

                        <button
                            onClick={handleSubmit}
                            disabled={isUploading || items.length === 0}
                            className="w-full md:w-auto px-8 py-3 md:py-2.5 bg-primary text-primary-foreground font-bold rounded-lg shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUploading ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                                    <span>Uploading... {uploadProgress}%</span>
                                </div>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    Submit Scan
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};
